import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { registryError, type IdentityFormValues } from './IdentityModel';

/**
 * Validação da atribuição de identidade (T5, FR-045, FR-046).
 *
 * O nome (slug), o namespace e a service account são chaves no middleware e no
 * SPIFFE ID: minúsculas, números e hífen. A imagem é o **repositório** — tag e
 * digest vêm da release, não da atribuição — e precisa estar no registry
 * configurado (FR-046, bloqueante).
 */

const NAME_K_8S = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/**
 * `<tipo>.<nome>` ou `<tipo>/<nome>` (aim-xsk.7): o controller de topo do pod. É a
 * mesma regra de `domain.ParseWorkload` no backend — os tipos que o admission aceita
 * e o nome DNS-1123 (pode ter ponto). A forma da esteira (`/`) é aceita e o servidor
 * grava com ponto.
 */
export const WORKLOAD = new RegExp(
  '^(deployment|statefulset|daemonset|job|cronjob|rollout)[./]' +
    '[a-z0-9]([-a-z0-9]*[a-z0-9])?(\\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$',
);

export function identitySchema(
  registryPrefix: string | null,
  /**
   * Slugs que o `ambiente_declarado` aceita — os SELECIONÁVEIS do cadastro do
   * tenant (Lote 3 · F1-c). Eram `['dev','hmg','prod']` escritos aqui.
   *
   * Lista VAZIA não valida nada além do vazio, e é o certo: sem cadastro (ou antes
   * de ele chegar) o campo só aceita «não declarado». Aceitar qualquer coisa
   * enquanto a lista não carrega deixaria passar um valor que o servidor recusa —
   * e a pessoa veria o 422 depois de clicar em salvar, que é o pior instante.
   */
  environmentSlugs: readonly string[],
): yup.ObjectSchema<IdentityFormValues> {
  return yup.object({
    slug: yup
      .string()
      .trim()
      .required(t('identities.form.validation.slugRequired'))
      .matches(NAME_K_8S, t('identities.form.validation.charset')),
    namespace: yup
      .string()
      .trim()
      .required(t('identities.form.validation.namespaceRequired'))
      .matches(NAME_K_8S, t('identities.form.validation.charset')),
    workload: yup
      .string()
      .trim()
      .required(t('identities.form.validation.workloadRequired'))
      .matches(WORKLOAD, t('field.workloadInvalid')),
    // A obrigatoriedade depende do template do site e fica com o servidor
    // (`serviceAccountRequiredByTemplate`); aqui só a forma.
    service_account: yup
      .string()
      .trim()
      .defined()
      .test('rfc1123', t('field.serviceAccountInvalid'), (valor) => !valor || NAME_K_8S.test(valor)),
    imagem: yup
      .string()
      .trim()
      .required(t('identities.form.validation.imageRequired'))
      .test(
        'sem-tag',
        t('field.repositoryWithoutTagOrDigest'),
        (valor) => !valor || (!valor.includes(':') && !valor.includes('@')),
      )
      .test(
        'registry',
        // A mensagem depende do que está configurado, então é montada aqui.
        () => registryError(registryPrefix ?? ''),
        (valor) => {
          // Sem saber o registry configurado, quem recusa é o servidor (422).
          if (!registryPrefix || !valor) return true;
          return valor.startsWith(`${registryPrefix}/`);
        },
      ),
    papel: yup.string().trim().nullable().defined(),
    especialidade: yup.string().defined(),
    descricao: yup.string().defined(),
    // O dono é **obrigatório na atribuição** (28/08), como o do resource em T7:
    // atribuir sem dizer de quem é o agente deixa a governança sem a quem
    // perguntar. O servidor recusa igual (422 `dados_invalidos`, campo `dono`);
    // esta checagem só evita a ida.
    dono: yup.string().trim().required(t('field.ownerRequired')),
    // A lista vem do CADASTRO, e o `''` continua na frente: «não declarado» é
    // valor legítimo do campo (o fallback humano da D-6 é opcional), e não a
    // ausência de escolha.
    ambiente_declarado: yup
      .string()
      .defined()
      .oneOf(['', ...environmentSlugs], t('field.declaredEnvironmentInvalid')),
  });
}

/** Resolver do formulário, revalidando a cada tecla (a prévia do SPIFFE segue a digitação). */
export function useIdentityValidation(
  registryPrefix: string | null,
  environmentSlugs: readonly string[],
) {
  // O `locale` não aparece no corpo e ainda assim é dependência: as mensagens
  // são montadas na construção do schema, então trocar de idioma com o drawer
  // aberto deixaria a validação no idioma anterior até o próximo `mount`.
  const locale = useLocale();
  // `environmentSlugs.join()` como dependência, e não o array: a lista é recriada
  // a cada render pelo `select` do react-query, então comparar por referência
  // reconstruiria o schema a cada tecla — e o resolver novo faz o formulário
  // revalidar tudo, incluindo a prévia do SPIFFE que segue a digitação.
  const slugsKey = environmentSlugs.join(',');
  const schema = useMemo(
    () => identitySchema(registryPrefix, environmentSlugs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registryPrefix, locale, slugsKey],
  );
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}
